import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useContext } from "react";
import { Button, Grid, Stack, TextField, Typography } from "@mui/material";
import { Link, useParams } from "react-router-dom";

import { createCoreNetworkMember, listCoreNetworkMembers, onboardArtworkDesignerTalent } from "../../../services/coreNetwork";
import { getDecoratorsDetails } from "../../../services/decorator";
import { AuthContext } from "../../../helpers/AuthContext";
import { CORE_NETWORK_PERMISSIONS, hasCoreNetworkPermission } from "../../../helpers/coreNetworkPermissions";
import {
  CoreNetworkMemberAvatar,
  CoreNetworkShell,
  CORE_NETWORK_LABEL,
  ErrorState,
  JsonListChips,
  LoadingState,
  PagedTable,
  SectionCard,
  StatusChip,
  buildCoreNetworkPath,
  normalizeJsonObject,
  safeArray,
  toTitleCase,
} from "./coreNetworkUiShared";
import CoreNetworkMemberForm from "./components/CoreNetworkMemberForm";

export default function CoreNetworkTalentList() {
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [members, setMembers] = useState([]);
  const [decorators, setDecorators] = useState([]);
  const [search, setSearch] = useState("");
  const [memberType, setMemberType] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const canManageMembers = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MANAGE_MEMBERS, instanceUniqueId);

  const loadMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [response, decoratorsResponse] = await Promise.all([
        listCoreNetworkMembers({
          instanceUniqueId,
          ...(search ? { search } : {}),
          ...(memberType ? { memberType } : {}),
        }),
        getDecoratorsDetails(instanceUniqueId),
      ]);
      setMembers(safeArray(response.members));
      setDecorators(safeArray(decoratorsResponse?.result));
    } catch (loadError) {
      setError(
        loadError?.response?.data?.message ||
          loadError.message ||
          "Unable to load Core Network talent."
      );
    } finally {
      setLoading(false);
    }
  }, [instanceUniqueId, memberType, search]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const sortedMembers = useMemo(
    () => [...members].sort((a, b) => String(a.displayName || "").localeCompare(String(b.displayName || ""))),
    [members]
  );

  const handleCreateMember = async (payload) => {
    try {
      setSaving(true);
      setError("");
      if (payload.useArtworkDesignerPreset) {
        await onboardArtworkDesignerTalent({
          ...payload,
          instanceUniqueId,
        });
      } else {
        await createCoreNetworkMember(payload);
      }
      setDialogOpen(false);
      await loadMembers();
    } catch (saveError) {
      setError(saveError?.response?.data?.message || saveError.message || "Unable to create member.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CoreNetworkShell
      title="Core Network Talent"
      breadcrumb={`${CORE_NETWORK_LABEL} / Talent`}
    >
      <Stack spacing={3}>
        {loading ? <LoadingState label="Loading talent..." /> : null}
        {!loading && error ? <ErrorState message={error} onAction={loadMembers} /> : null}
        <SectionCard
          title="Talent Directory"
          subtitle="Global talent profiles with instance-specific memberships layered on top."
          action={canManageMembers ? <Button variant="contained" onClick={() => setDialogOpen(true)}>Add Provider</Button> : null}
        >
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={5}>
              <TextField fullWidth label="Search talent" value={search} onChange={(event) => setSearch(event.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Member type" value={memberType} onChange={(event) => setMemberType(event.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ height: "100%", alignItems: "center" }}>
                <Button variant="outlined" onClick={loadMembers}>Refresh</Button>
              </Stack>
            </Grid>
          </Grid>
          {!loading ? (
            <PagedTable
              rows={sortedMembers}
              columns={[
                {
                  key: "displayName",
                  label: "Member",
                  render: (row) => (
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <CoreNetworkMemberAvatar member={row} size={42} />
                      <Stack spacing={0.5}>
                        <Typography
                          component={Link}
                          to={buildCoreNetworkPath(instanceUniqueId, `talent/${row.id}`)}
                          sx={{ fontWeight: 700, textDecoration: "none", color: "#103a71" }}
                        >
                          {row.displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.companyName || row.email || "-"}
                        </Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {row.profileType ? <StatusChip value={row.profileType} /> : null}
                          {row.providerKey ? <StatusChip value={row.providerKey} /> : null}
                          {normalizeJsonObject(row.metadataJson).defaultProofProvider ? (
                            <StatusChip value="default proofing provider" />
                          ) : null}
                        </Stack>
                      </Stack>
                    </Stack>
                  ),
                },
                {
                  key: "memberType",
                  label: "Type",
                  render: (row) => toTitleCase(row.profileType || row.memberType),
                },
                { key: "globalStatus", label: "Status", render: (row) => <StatusChip value={row.globalStatus} /> },
                {
                  key: "portalAccess",
                  label: "Portal",
                  render: (row) => <StatusChip value={row.portalAccess?.status || "not_invited"} />,
                },
                { key: "networkTypesJson", label: "Networks", render: (row) => <JsonListChips values={row.networkTypesJson} emptyLabel="-" /> },
                {
                  key: "capabilities",
                  label: "Capabilities",
                  render: (row) => (
                    <JsonListChips
                      values={safeArray(row.capabilitiesJson).length ? row.capabilitiesJson : safeArray(row.capabilities).map((capability) => capability.capabilityType)}
                      emptyLabel="-"
                    />
                  ),
                },
              ]}
              emptyTitle="No Core Network profiles found"
              emptyDescription="Add the first talent profile to start building your curated Core Network supply."
            />
          ) : null}
        </SectionCard>

        {canManageMembers ? (
          <CoreNetworkMemberForm
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onSubmit={handleCreateMember}
            submitting={saving}
            decoratorOptions={decorators}
          />
        ) : null}
      </Stack>
    </CoreNetworkShell>
  );
}
